import React, { useState } from "react";
import { StudentByMobileResponse } from "../types";
import "../styles/StudentDetails.css";

interface StudentDetailsProps {
  profile: StudentByMobileResponse;
  onChangeNumber: () => void;
  onMakePayment: () => void;
}

const IMAGE_BASE_URL =
  process.env.REACT_APP_API_GET_IMAGE_URL ||
  "https://localhost:7184/uploads/students/";

const StudentDetails: React.FC<StudentDetailsProps> = ({
  profile,
  onChangeNumber,
  onMakePayment,
}) => {
  const { student, lastPayment } = profile;
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const profileImageUrl = student.imagePath
    ? `${IMAGE_BASE_URL}${student.imagePath}`
    : null;

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getValidityStatus = (validityTo?: string | null) => {
    if (!validityTo) {
      return { days: 0, isOverdue: false, label: "-" };
    }

    const today = new Date();
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const parsed = new Date(validityTo);
    if (Number.isNaN(parsed.getTime())) {
      return { days: 0, isOverdue: false, label: "-" };
    }

    const endDate = new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate()
    );

    const diffDays = Math.round(
      (endDate.getTime() - todayOnly.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays < 0) {
      const overdueDays = Math.abs(diffDays);
      return {
        days: overdueDays,
        isOverdue: true,
        label: `${overdueDays} day${overdueDays !== 1 ? "s" : ""} overdue`,
      };
    }

    if (diffDays === 0) {
      return {
        days: 0,
        isOverdue: false,
        label: "Expires today",
      };
    }

    return {
      days: diffDays,
      isOverdue: false,
      label: `${diffDays} day${diffDays !== 1 ? "s" : ""} remaining`,
    };
  };

  const validityStatus = lastPayment
    ? getValidityStatus(lastPayment.validityTo)
    : null;

  const genderLabel =
    student.genderCode === "M"
      ? "Male"
      : student.genderCode === "F"
      ? "Female"
      : student.genderCode || "-";

  return (
    <div className="student-details-page">
      <div className="student-details-topbar">
        <div>
          <h1>Student Details</h1>
          <p>Om Sai Reading Room</p>
        </div>
        <button type="button" className="change-number-btn" onClick={onChangeNumber}>
          Change number
        </button>
      </div>

      {validityStatus &&
        !validityStatus.isOverdue &&
        validityStatus.days < 3 && (
        <div className="warning-banner">
          Warning: Your membership {validityStatus.label.toLowerCase()}. Please
          renew soon.
        </div>
      )}

      {validityStatus?.isOverdue && (
        <div className="warning-banner overdue-banner">
          Warning: Your membership is {validityStatus.label}. Please renew
          immediately.
        </div>
      )}

      <div className="profile-header">
        <div
          className={`profile-avatar${profileImageUrl ? " clickable" : ""}`}
          onClick={() => profileImageUrl && setIsImageModalOpen(true)}
          role={profileImageUrl ? "button" : undefined}
          tabIndex={profileImageUrl ? 0 : undefined}
          onKeyDown={(e) => {
            if (profileImageUrl && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              setIsImageModalOpen(true);
            }
          }}
        >
          {profileImageUrl ? (
            <img
              src={profileImageUrl}
              alt={student.fullName || "Student"}
              className="profile-image"
            />
          ) : (
            <span>{student.fullName?.charAt(0).toUpperCase() || "S"}</span>
          )}
        </div>
        <div className="profile-header-info">
          <h2>{student.fullName}</h2>
          <p>Student ID: {student.studentDetailsId}</p>
          <span className={`status-badge ${student.isActive ? "active" : "inactive"}`}>
            {student.isActive ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="profile-section">
        <h3>Personal Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Full Name</label>
            <p>{student.fullName || "-"}</p>
          </div>
          <div className="info-item">
            <label>Email Address</label>
            <p>{student.emailId || "-"}</p>
          </div>
          <div className="info-item">
            <label>Mobile Number</label>
            <p>{student.mobileNumber || "-"}</p>
          </div>
          <div className="info-item">
            <label>Alternate Number</label>
            <p>{student.alternateNumber || "-"}</p>
          </div>
          <div className="info-item">
            <label>Aadhar Number</label>
            <p>{student.aadharNumber || "-"}</p>
          </div>
          <div className="info-item">
            <label>Optional ID</label>
            <p>{student.optionalIdentification || "-"}</p>
          </div>
          <div className="info-item">
            <label>Gender</label>
            <p>{genderLabel}</p>
          </div>
          <div className="info-item">
            <label>Joining Date</label>
            <p>{formatDate(student.dateOfJoining)}</p>
          </div>
        </div>
      </div>

      <div className="profile-section">
        <h3>Guardian Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Guardian Name</label>
            <p>{student.guardianName || "-"}</p>
          </div>
          <div className="info-item">
            <label>Guardian Mobile</label>
            <p>{student.guardianMobileNumber || "-"}</p>
          </div>
        </div>
      </div>

      <div className="profile-section">
        <h3>Last Payment</h3>
        {lastPayment ? (
          <div className="info-grid">
            <div className="info-item">
              <label>Amount</label>
              <p>₹{lastPayment.amount}</p>
            </div>
            <div className="info-item">
              <label>Payment Date</label>
              <p>{formatDate(lastPayment.paymentDate)}</p>
            </div>
            <div className="info-item">
              <label>Validity From</label>
              <p>{formatDate(lastPayment.validityFrom)}</p>
            </div>
            <div className="info-item">
              <label>Validity To</label>
              <p>{formatDate(lastPayment.validityTo)}</p>
            </div>
            <div className="info-item">
              <label>Validity Days</label>
              <p>{lastPayment.validityDays}</p>
            </div>
            <div className="info-item">
              <label>{validityStatus?.isOverdue ? "Overdue" : "Remaining"}</label>
              <p
                className={
                  validityStatus?.isOverdue
                    ? "status-overdue"
                    : validityStatus && validityStatus.days < 3
                    ? "status-warning"
                    : "status-ok"
                }
              >
                {validityStatus?.label || "-"}
              </p>
            </div>
          </div>
        ) : (
          <p className="empty-payment">No payment record found.</p>
        )}

        {(!lastPayment ||
          (validityStatus &&
            (validityStatus.isOverdue || validityStatus.days <= 3))) && (
          <div className="make-payment-action">
            <button
              type="button"
              className="make-payment-btn"
              onClick={onMakePayment}
            >
              Make Payment
            </button>
          </div>
        )}
      </div>

      {isImageModalOpen && profileImageUrl && (
        <div
          className="image-modal-overlay"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="image-modal-close"
              onClick={() => setIsImageModalOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={profileImageUrl}
              alt={student.fullName || "Student"}
              className="image-modal-img"
            />
            <p className="image-modal-name">{student.fullName}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDetails;
